import type { AppId, AppAccessConfig } from '../types/app-access'

/**
 * Maps each ClientPortal app to its access rule.
 *
 *
 * @example
 * dashboard: { enabled: true },  // no permission required
 * communities: {
 *   enabled: true,
 *   permission: 'communitiesEnabled',
 *   conditions: [requirePermission('membershipEnabled')],
 * }
 */
export const APP_ACCESS_CONFIG: AppAccessConfig = {
  dashboard: { enabled: true },
  courses: { enabled: true, permission: 'membershipEnabled' },
  communities: { enabled: true, permission: 'communitiesEnabled' },
  documents: { enabled: true, permission: 'sharedFilesEnabled' },
  invoices: { enabled: true, permission: 'subscriptionsEnabled' },
  estimates: { enabled: true, permission: 'estimatesEnabled' },
  contracts: { enabled: true, permission: 'contractsEnabled' },
  appointments: { enabled: true, permission: 'appointmentsEnabled' },
  affiliates: { enabled: true, permission: 'affiliateManagerEnabled' },
  loyalty: { enabled: true, permission: 'loyaltyEnabled' },
}

/**
 * Route path prefixes for app access check (order: longest first for correct matching)
 */
export const APP_ROUTE_PREFIXES: { path: string; appId: AppId }[] = [
  { path: '/dashboard', appId: 'dashboard' },
  { path: '/courses', appId: 'courses' },
  { path: '/communities', appId: 'communities' },
  { path: '/shared-files', appId: 'documents' },
  { path: '/documents', appId: 'documents' },
  { path: '/invoices', appId: 'invoices' },
  { path: '/estimates', appId: 'estimates' },
  { path: '/contracts', appId: 'contracts' },
  { path: '/appointments', appId: 'appointments' },
  { path: '/affiliates', appId: 'affiliates' },
  { path: '/loyalty', appId: 'loyalty' },
]

/**
 * Get app ID from route path for access check
 */
export function getAppIdFromPath(path: string): AppId | null {
  if (!path || path === '/') return null
  const match = APP_ROUTE_PREFIXES.find(({ path: p }) => path === p || path.startsWith(p + '/'))
  return match?.appId ?? null
}

/** Distinct app ids that own a route (derived from the prefix map — single source of truth). */
const APP_IDS: AppId[] = [...new Set(APP_ROUTE_PREFIXES.map(({ appId }) => appId))]

/**
 * App ID from a Nuxt route name (hyphen-delimited segments) — classifies routes whose URL has no
 * section prefix, e.g. GoKollab tenant slugs. Exact-segment match (no substrings), checks every appId.
 */
export function getAppIdFromRouteName(routeName: string | undefined | null): AppId | null {
  if (!routeName) return null
  const segments = routeName.split('-')
  return APP_IDS.find(appId => segments.includes(appId)) ?? null
}
